import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface Course {
  value: string;
  label: string;
}

export const useCourses = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCourses();
    
    // Escuchar eventos de actualización de cursos
    const handleCoursesUpdate = () => {
      loadCourses();
    };
    
    window.addEventListener('coursesUpdated', handleCoursesUpdate);
    
    return () => {
      window.removeEventListener('coursesUpdated', handleCoursesUpdate);
    };
  }, []);

  const loadCourses = async () => {
    try {
      const { data, error } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'available_courses')
        .eq('is_active', true)
        .maybeSingle();

      if (error) throw error;

      if (data?.value) {
        // Parse the courses - puede ser un string separado por comas o un array JSON
        let coursesArray: string[] = [];
        
        if (typeof data.value === 'string') {
          // Si es un string, dividir por comas
          coursesArray = data.value.split(',').map(course => course.trim().replace(/^["']|["']$/g, '')).filter(course => course.length > 0);
        } else if (Array.isArray(data.value)) {
          // Si ya es un array, convertir cada elemento a string y limpiar comillas
          coursesArray = data.value.map(course => String(course).replace(/^["']|["']$/g, '')).filter(course => course.length > 0);
        }
        
        const mappedCourses = coursesArray.map((course: string) => ({
          value: course,
          label: course
        }));
        setCourses(mappedCourses);
      } else {
        // Default courses if not configured
        setCourses([
          { value: '1º A', label: '1º A' },
          { value: '1º B', label: '1º B' },
          { value: '2º A', label: '2º A' },
          { value: '2º B', label: '2º B' },
          { value: '3º A', label: '3º A' },
          { value: '3º B', label: '3º B' },
          { value: '4º A', label: '4º A' },
          { value: '4º B', label: '4º B' },
          { value: '5º A', label: '5º A' },
          { value: '5º B', label: '5º B' },
          { value: '6º A', label: '6º A' },
          { value: '6º B', label: '6º B' },
          { value: '1º ESO A', label: '1º ESO A' },
          { value: '1º ESO B', label: '1º ESO B' },
          { value: '2º ESO A', label: '2º ESO A' },
          { value: '2º ESO B', label: '2º ESO B' },
          { value: '3º ESO A', label: '3º ESO A' },
          { value: '3º ESO B', label: '3º ESO B' },
          { value: '4º ESO A', label: '4º ESO A' },
          { value: '4º ESO B', label: '4º ESO B' },
        ]);
      }
    } catch (error) {
      // Use default courses on error
      setCourses([
        { value: '1º A', label: '1º A' },
        { value: '1º B', label: '1º B' },
        { value: '2º A', label: '2º A' },
        { value: '2º B', label: '2º B' },
        { value: '3º A', label: '3º A' },
        { value: '3º B', label: '3º B' },
        { value: '4º A', label: '4º A' },
        { value: '4º B', label: '4º B' },
        { value: '5º A', label: '5º A' },
        { value: '5º B', label: '5º B' },
        { value: '6º A', label: '6º A' },
        { value: '6º B', label: '6º B' },
        { value: '1º ESO A', label: '1º ESO A' },
        { value: '1º ESO B', label: '1º ESO B' },
        { value: '2º ESO A', label: '2º ESO A' },
        { value: '2º ESO B', label: '2º ESO B' },
        { value: '3º ESO A', label: '3º ESO A' },
        { value: '3º ESO B', label: '3º ESO B' },
        { value: '4º ESO A', label: '4º ESO A' },
        { value: '4º ESO B', label: '4º ESO B' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return {
    courses,
    loading,
    refetch: loadCourses,
  };
};